import React from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { ClientAcquisition } from '../../../types/utility.types';

interface ClientAcquisitionTimelineProps {
  data: ClientAcquisition[];
  isLoading: boolean;
}

const ClientAcquisitionTimeline: React.FC<ClientAcquisitionTimelineProps> = ({ data, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Client Acquisition Timeline
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Format the period for display (YYYY-MM to Month YYYY)
  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('default', { month: 'short', year: 'numeric' });
  };

  // Sort data by period
  const chartData = [...data].sort((a, b) => {
    const periodA = a.period.split('-').map(Number);
    const periodB = b.period.split('-').map(Number);
    
    if (periodA[0] !== periodB[0]) {
      return periodA[0] - periodB[0];
    }
    return periodA[1] - periodB[1];
  }).map(item => ({
    ...item,
    formattedPeriod: formatPeriod(item.period)
  }));

  // Calculate the trend line (cumulative count)
  let cumulativeCount = 0;
  const chartDataWithTrend = chartData.map(item => {
    cumulativeCount += item.count;
    return {
      ...item,
      cumulative: cumulativeCount
    };
  });

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Client Acquisition Timeline
        </Typography>
        {chartData.length > 0 ? (
          <Box sx={{ height: 300 }}>
            <LineChart
              series={[
                {
                  data: chartDataWithTrend.map(d => d.count),
                  label: 'New Clients',
                  color: theme.palette.primary.main,
                  yAxisKey: 'left',
                },
                {
                  data: chartDataWithTrend.map(d => d.cumulative),
                  label: 'Total Clients',
                  color: '#ff9800',
                  yAxisKey: 'right',
                },
              ]}
              xAxis={[
                {
                  data: chartDataWithTrend.map(d => d.formattedPeriod),
                  scaleType: 'point',
                },
              ]}
              yAxis={[
                { id: 'left' },
                { id: 'right' },
              ]}
              height={300}
              sx={{
                '& .MuiLineElement-series-Total_Clients': {
                  strokeWidth: 3,
                },
                '& .MuiPopper-root': {
                  '& .MuiChartsTooltip-root': {
                    background: 'rgba(30, 41, 59, 0.95) !important',
                    backdropFilter: 'blur(12px) !important',
                    border: '1px solid rgba(255, 255, 255, 0.1) !important',
                    borderRadius: '8px !important',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3) !important',
                  },
                  '& .MuiChartsTooltip-table': {
                    color: 'rgba(255, 255, 255, 0.9) !important',
                  },
                },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientAcquisitionTimeline;
